import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { RestaurantCard } from "./RestaurantCard";
import type { Discount, Restaurant, RestaurantId } from "@/types/restaurant";

// Full-bleed on phone: pull the track out of the 24px container gutter, then add
// it back as padding + scroll-padding so the first card lines up with the
// heading and the rest scroll off the right screen edge. Reset from sm: up, where
// the strip stays inside the centered container. Shared with NearbyStrip.
export const TRACK =
  "flex items-stretch gap-4 overflow-x-auto pb-2 -mx-6 px-6 [scroll-padding-left:1.5rem] snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0 sm:[scroll-padding-left:0]";
export const CARD_WRAP = "flex w-[85%] shrink-0 snap-start sm:w-80";

export interface VenueStripProps {
  /** Section heading (already translated). */
  title: string;
  /** Small lucide icon rendered before the title (tint it text-pulse). */
  icon?: ReactNode;
  /** Optional one-liner under the heading. */
  subtext?: string;
  /** Strip content, in final order: each venue + whether it's currently open. */
  items: { r: Restaurant; open: boolean }[];
  /** Active discounts keyed by venue — passed straight through to the card. */
  discountsByRestaurant: Record<RestaurantId, Discount[]>;
  /** Still resolving content — show the header + skeletons instead of nothing. */
  loading?: boolean;
  onSelect: (restaurant: Restaurant) => void;
}

/**
 * Generic horizontal, swipeable strip of venue cards — the presentational
 * sibling of NearbyStrip (which keeps its geo state machine and only shares
 * the primitives above). Renders NOTHING at all — no header, no gap — when
 * it has no items and isn't loading, so conditional strips (fast venues)
 * disappear cleanly.
 */
export function VenueStrip({
  title,
  icon,
  subtext,
  items,
  discountsByRestaurant,
  loading = false,
  onSelect,
}: VenueStripProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // Show the desktop arrows only when the track actually overflows, so a strip
  // with a couple of venues doesn't get dead controls.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth - el.clientWidth > 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length, loading]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 360), behavior: "smooth" });
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="mt-6 sm:mt-8" aria-label={title}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="inline-flex items-center gap-2 font-display text-lg tracking-tight text-zinc-100 light:text-zinc-900 sm:text-xl">
            {icon}
            {title}
          </h2>
          {subtext && (
            <p className="mt-0.5 text-xs leading-relaxed text-zinc-400 light:text-zinc-600">{subtext}</p>
          )}
        </div>
        {!loading && overflowing && (
          <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
            <ArrowBtn dir="left" onClick={() => scrollBy(-1)} />
            <ArrowBtn dir="right" onClick={() => scrollBy(1)} />
          </div>
        )}
      </div>

      {loading ? (
        <div className="relative mt-3">
          <div className={TRACK} aria-hidden>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={CARD_WRAP}>
                <div className="h-72 w-full animate-pulse rounded-2xl border border-white/10 light:border-black/10 bg-zinc-900/50 light:bg-white/60" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="relative mt-3">
          {/* edge fade masks (desktop polish; mobile bleeds to the screen edge) */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-8 bg-gradient-to-r from-background to-transparent sm:block" aria-hidden />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-8 bg-gradient-to-l from-background to-transparent sm:block" aria-hidden />
          <div ref={scroller} className={TRACK}>
            {items.map(({ r, open }) => (
              <div key={r.id} className={CARD_WRAP}>
                <RestaurantCard
                  restaurant={r}
                  isOpen={open}
                  discounts={discountsByRestaurant[r.id]}
                  onSelect={onSelect}
                  className="h-full"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function ArrowBtn({ dir, onClick }: { dir: "left" | "right"; onClick: () => void }) {
  const { t } = useTranslation();
  const Icon = dir === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t(dir === "left" ? "SCROLL_LEFT" : "SCROLL_RIGHT")}
      className="grid h-8 w-8 place-items-center rounded-full border border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 text-zinc-300 light:text-zinc-700 transition hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900"
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
